import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { AppContextService, AppContext } from './app-context.service';
import { RealAiApiService, AiApiRequest, AiApiResponse } from './real-ai-api.service';
import { UserService } from './user.service';
import { PatientAiContextService } from './patient-ai-context.service';
import { environment } from '../../environments/environment';
import { ProgramService } from '../program/program.service';
export type MessageType = 'user' | 'ai' | 'system' | 'error';
export interface ChatMessage {
  id: string;
  type: MessageType;
  content: string;
  timestamp: Date;
  context?: AppContext;
}

/**
 * Interface for suggested prompts
 */
export interface SuggestedPrompt {
  text: string;
  icon?: string;
  task?: string;
}
@Injectable({
  providedIn: 'root'
})
export class AiChatService {
  private messagesSubject = new BehaviorSubject<ChatMessage[]>([]);
  public messages$: Observable<ChatMessage[]> = this.messagesSubject.asObservable();

  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$: Observable<boolean> = this.loadingSubject.asObservable();

  private suggestedPromptsSubject = new BehaviorSubject<SuggestedPrompt[]>([]);
  public suggestedPrompts$: Observable<SuggestedPrompt[]> = this.suggestedPromptsSubject.asObservable();

  private currentContext: AppContext;
  private readonly MAX_MESSAGES = 50;
  public responseFronGet: any;

  constructor(
    private appContextService: AppContextService,
    private realAiApiService: RealAiApiService,
    private userService: UserService,
    private patientAiContextService: PatientAiContextService,
    private programService: ProgramService
  ) {
    this.appContextService.context$.subscribe(context => {
      this.currentContext = context;
      this.updateSuggestedPrompts(context);
    });

    this.currentContext = this.appContextService.getContext();
    this.updateSuggestedPrompts(this.currentContext);
  }

  public sendMessage(messageText: string, taskId?: string): void {
    if (!messageText || !messageText.trim()) {
      return;
    }

    const userMessage: ChatMessage = {
      id: this.generateMessageId(),
      type: 'user',
      content: messageText.trim(),
      timestamp: new Date(),
      context: this.currentContext
    };
    this.addMessage(userMessage);

    this.loadingSubject.next(true);

    const isEnrolledPatientPrompt =
      taskId &&
      ((this.currentContext.active_tab && this.currentContext.active_tab.startsWith('app-enrolled-patient')) ||
       (this.currentContext.screen_name && this.currentContext.screen_name.startsWith('app-enrolled-patient'))) &&
      this.currentContext.patient_id;

    const isDashboardPrompt = taskId === 'summarize_current_census' || taskId === 'weekly_operational_report';

    let contextPromise: Promise<any>;

    if (isEnrolledPatientPrompt) {
      contextPromise = this.patientAiContextService.getAiContext(this.currentContext.patient_id);
    } else if (isDashboardPrompt) {
      contextPromise = this.getDashboardSnapshot(taskId);
    } else {
      contextPromise = Promise.resolve(null);
    }

    contextPromise.then(contextData => {
      const request: AiApiRequest = {
        message:      messageText.trim(),
        user_id:      this.userService.current_user?.id,
        practice_id:  this.userService.current_user?.profileable_id,
        screen:       this.currentContext.screen_name,
        active_tab:   this.currentContext.active_tab,
        patient_id:   this.currentContext.patient_id?.toString(),
        program_id:   this.currentContext.program_id,
        timestamp:    new Date().toISOString(),
        session_id:   'S1',
        chat_id:      'C1',
        environment:  (environment as any).envName || 'unknown'
      };

      if (taskId) {
        request.task = taskId;
      }

      if (contextData && Object.keys(contextData).length > 0) {
        if (isDashboardPrompt) {
          request.structured_dashboard_snapshot = contextData;
          console.log('📊 Dashboard context attached to AI request:', contextData);
        } else {
          request.patient_data = contextData;
        }
      }

      this.realAiApiService.sendMessage(request).subscribe(
        (response) => {
          const aiMessage: ChatMessage = {
            id: response['chat_id'],
            type: 'ai',
            content: response['answer'],
            timestamp: new Date(),
            context: this.currentContext
          };
          this.addMessage(aiMessage);
          this.loadingSubject.next(false);
        },
        (error) => {
          const errorMessage: ChatMessage = {
            id: this.generateMessageId(),
            type: 'error',
            content: 'I apologize, but I\'m having trouble connecting right now. Please try again in a moment.',
            timestamp: new Date()
          };
          this.addMessage(errorMessage);
          this.loadingSubject.next(false);
          console.error('AI API Error:', error);
        }
      );
    });
  }


  getCurrentMessage() {
    this.realAiApiService.getCurrentUserChats().then(resp => {
      const aiMessage: ChatMessage = {
        id: this.generateMessageId(),
        type: 'ai',
        content: resp,
        timestamp: new Date(resp.timestamp),
        context: this.currentContext
      };
      this.responseFronGet = resp

    }).catch(error => {
      const errorMessage: ChatMessage = {
        id: this.generateMessageId(),
        type: 'error',
        content: 'I apologize, but I\'m having trouble connecting right now. Please try again in a moment.',
        timestamp: new Date()
      };
      this.addMessage(errorMessage);
      this.loadingSubject.next(false);

    });
  }

  /**
   * Add a message to the chat history
   * @param message - The message to add
   */
  private addMessage(message: ChatMessage): void {
    const currentMessages = this.messagesSubject.value;
    const updatedMessages = [...currentMessages, message];

    if (updatedMessages.length > this.MAX_MESSAGES) {
      updatedMessages.shift();
    }

    this.messagesSubject.next(updatedMessages);
  }

  /**
   * Add a system message
   * @param content - Message content
   */
  private addSystemMessage(content: string): void {
    const message: ChatMessage = {
      id: this.generateMessageId(),
      type: 'system',
      content,
      timestamp: new Date()
    };
    this.addMessage(message);
  }

  /**
   * Generate a unique message ID
   */
  private generateMessageId(): string {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private readonly SCREEN_PROMPTS: Record<string, SuggestedPrompt[]> = {
    'program-dashboard': [
      { text: 'Summarize current census', task: 'summarize_current_census' },
      { text: 'Generate Weekly Operational Report', task: 'weekly_operational_report' },
    ],

    'app-enrolled-patient': [
      { text: 'Create Care Plan', task: 'careplan' },
      { text: 'Generate Risk Overview', task: 'generate_risk_overview' },
      { text: 'Draft Escalation Report', task: 'draft_escalation_report' },
    ],

    // ── All other screens (fallback)
    'general': [
      { text: 'What can you help me with?', task: 'help' },
    ],
  };

  private updateSuggestedPrompts(context: AppContext): void {
    if (context.active_tab && context.active_tab.startsWith('app-enrolled-patient')) {
      this.suggestedPromptsSubject.next(this.SCREEN_PROMPTS['app-enrolled-patient']);
      return;
    }

    if (context.active_tab && this.SCREEN_PROMPTS[context.active_tab]) {
      this.suggestedPromptsSubject.next(this.SCREEN_PROMPTS[context.active_tab]);
      return;
    }

    if (context.screen_name && context.screen_name.startsWith('app-enrolled-patient')) {
      this.suggestedPromptsSubject.next(this.SCREEN_PROMPTS['app-enrolled-patient']);
      return;
    }

    if (context.screen_name && this.SCREEN_PROMPTS[context.screen_name]) {
      this.suggestedPromptsSubject.next(this.SCREEN_PROMPTS[context.screen_name]);
      return;
    }

    this.suggestedPromptsSubject.next(this.SCREEN_PROMPTS['general']);
  }

  public getSuggestedPrompts(): SuggestedPrompt[] {
    return this.suggestedPromptsSubject.value;
  }

  public getMessages(): ChatMessage[] {
    return this.messagesSubject.value;
  }

  /**
   * Clear all chat messages
   */
  public clearHistory(): void {
    this.messagesSubject.next([]);
    this.addSystemMessage('How can I help you?');
  }

  /**
   * Check if chat is currently loading
   * @returns Loading state
   */
  public isLoading(): boolean {
    return this.loadingSubject.value;
  }

  private getDashboardSnapshot(taskId: string): Promise<any> {
    const filter = { year: new Date().getFullYear(), month: new Date().getMonth() + 1 };

    const alertDays = taskId === 'weekly_operational_report' ? 'last_seven_days' : 'last_thirty_days';

    return this.programService.getAiDashboardSnapshot(alertDays).then(snapshot => {
      return snapshot;
    }).catch(err => {
      console.error('Error fetching dashboard snapshot:', err);
      return {};
    });
  }
}
